// ==========================================================================
// El paso de una pantalla a otra, en el teléfono.
//
// En una app nativa la pantalla nueva ENTRA (desde la derecha al avanzar,
// desde la izquierda al volver) y eso dice, sin palabras, hacia dónde se ha
// ido. En el ERP las pantallas se cambiaban con un fundido de 120 ms, que en
// un monitor está bien —con un ratón nadie espera que la página se deslice—
// y en un teléfono se lee como una web recargando.
//
// Por eso hay dos nombres de transición y no uno:
//
//   · `fade` (base.css): con ratón, y SIEMPRE que el sistema pida menos
//     movimiento. La regla global de tokens.css ya acorta toda transición a
//     nada en ese caso; elegir aquí `fade` es no depender solo de ella.
//   · `page-forward` / `page-back` (AppLayout.vue): con el dedo
//     (`pointer: coarse`). Deslizar 24 px y fundir, unos 200 ms.
//
// Se decide con `pointer: coarse` y no con el ancho de la ventana: una tablet
// apaisada es tan táctil como un teléfono, y un portátil estrecho no lo es.
// ==========================================================================

import { ref, computed, onScopeDispose, getCurrentScope } from 'vue'

/** Lo que dura la entrada de la pantalla nueva en el teléfono. */
export const PAGE_TRANSITION_MS = 200

/**
 * El nombre de la `<Transition>` del RouterView.
 * @param {{ coarse: boolean, reducedMotion: boolean, back: boolean }} input
 */
export function pageTransitionName({ coarse, reducedMotion, back }) {
  if (reducedMotion || !coarse) return 'fade'
  return back ? 'page-back' : 'page-forward'
}

function mediaFlag(query) {
  const list = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query) : null
  const flag = ref(Boolean(list?.matches))
  const sync = () => { flag.value = Boolean(list?.matches) }
  list?.addEventListener?.('change', sync)
  if (getCurrentScope()) onScopeDispose(() => list?.removeEventListener?.('change', sync))
  return flag
}

/**
 * Para el armazón: el nombre vivo, y cómo decirle hacia dónde fue la última
 * navegación (lo sabe `scrollMemory`, que escucha el `popstate`).
 */
export function usePageTransition() {
  const coarse = mediaFlag('(pointer: coarse)')
  const reducedMotion = mediaFlag('(prefers-reduced-motion: reduce)')
  const back = ref(false)
  const name = computed(() => pageTransitionName({
    coarse: coarse.value,
    reducedMotion: reducedMotion.value,
    back: back.value,
  }))
  return { name, setBack: (value) => { back.value = Boolean(value) } }
}

// ── La barra de «yendo» ─────────────────────────────────────────────────────
//
// Entre el toque y la pantalla nueva hay un hueco: el código de esa pantalla
// se pide por la red la primera vez que se abre (las rutas son perezosas). Con
// cobertura floja eso es un segundo largo en el que la pantalla vieja sigue
// ahí, quieta, como si el toque no hubiera llegado. Una raya fina arriba lo
// dice — pero solo si tarda (`delay`), que en una red buena no se vea nunca.

export const NAV_PROGRESS_DELAY_MS = 150

/**
 * @param {import('vue-router').Router} router
 * @param {{ delay?: number }} [options]
 * @returns {import('vue').Ref<boolean>} si se está yendo a otra pantalla y tarda
 */
export function useNavigationProgress(router, { delay = NAV_PROGRESS_DELAY_MS } = {}) {
  const busy = ref(false)
  let timer = null
  const done = () => {
    clearTimeout(timer)
    timer = null
    busy.value = false
  }
  const stopBefore = router.beforeEach(() => {
    clearTimeout(timer)
    timer = setTimeout(() => { busy.value = true }, delay)
  })
  const stopAfter = router.afterEach(done)
  const stopError = router.onError(done)
  if (getCurrentScope()) {
    onScopeDispose(() => { done(); stopBefore(); stopAfter(); stopError() })
  }
  return busy
}
